/**
 * Export, deletion, and notifications (`core-api` §8, SPEC §10).
 *
 * Deletion asks for the child's name to be typed out. Not friction for its own
 * sake: a double confirmation is what separates "I meant to do this" from a
 * mis-tap on a phone, and the grace period does the rest.
 *
 * Web Push lives here too, and only for a child's device: **Telegram is a
 * parent-only channel** (CLAUDE.md rule 5). Baya and Mehdi have no Telegram
 * account, no application, no exposure — they get Web Push in this PWA and
 * nothing else.
 */

import { useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { ChildSummary, ExportOut } from "@/api/schema";
import { pushStatus, subscribeToPush, type PushStatus } from "@/push/subscribe";
import { Button, Card, ErrorNote, Screen } from "@/ui/primitives";

export function DataScreen() {
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [jobs, setJobs] = useState<Record<string, ExportOut>>({});
  const [confirmations, setConfirmations] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [push, setPush] = useState<PushStatus>("unknown");

  useEffect(() => {
    void call("listChildren")
      .then(setChildren)
      .catch((failure) =>
        setError(failure instanceof ApiError ? failure.message : "Chargement impossible."),
      );
    void pushStatus().then(setPush);
  }, []);

  return (
    <Screen title="Données et notifications">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Card title="Rappels de l'enfant sur cet appareil">
        <p className="muted small">
          Web Push, sur l'appareil de l'enfant. Aucune information sensible dans la notification :
          elle est lisible sur un écran verrouillé. Telegram reste réservé aux parents.
        </p>
        <p className="muted small">
          {push === "subscribed"
            ? "Cet appareil est abonné."
            : push === "denied"
              ? "Les notifications sont refusées dans le navigateur."
              : push === "unsupported"
                ? "Ce navigateur ne gère pas les notifications."
                : push === "server-unavailable"
                  ? "Le serveur n'enregistre pas encore les abonnements."
                  : "Cet appareil n'est pas abonné."}
        </p>
        <Button
          variant="secondary"
          disabled={push === "subscribed" || push === "unsupported" || push === "denied"}
          onClick={() => void subscribeToPush().then(setPush)}
        >
          Activer les rappels ici
        </Button>
      </Card>

      {children.map((child) => (
        <Card key={child.child_id} title={child.display_name}>
          <div className="row">
            <Button
              variant="secondary"
              onClick={() =>
                void call("requestExport", { params: { child_id: child.child_id } }).then((job) =>
                  setJobs((previous) => ({ ...previous, [child.child_id]: job })),
                )
              }
            >
              Exporter ses données
            </Button>
            {jobs[child.child_id] ? (
              <span className="muted small">
                export {jobs[child.child_id]?.status}
                {jobs[child.child_id]?.download_url ? (
                  <>
                    {" — "}
                    <a href={jobs[child.child_id]?.download_url ?? "#"}>télécharger</a>
                  </>
                ) : null}
              </span>
            ) : null}
          </div>

          <details className="deep">
            <summary>Supprimer les données de {child.display_name}</summary>
            <p className="muted small">
              Écrivez « {child.display_name} » pour confirmer. Trente jours de délai avant la
              purge, annulables.
            </p>
            <input
              className="input"
              value={confirmations[child.child_id] ?? ""}
              onChange={(event) =>
                setConfirmations((previous) => ({ ...previous, [child.child_id]: event.target.value }))
              }
              aria-label={`Confirmer en écrivant ${child.display_name}`}
            />
            <Button
              variant="danger"
              disabled={confirmations[child.child_id] !== child.display_name}
              onClick={() =>
                void call("requestDeletion", {
                  params: { child_id: child.child_id },
                  body: { confirm_display_name: child.display_name },
                }).catch((failure) =>
                  setError(failure instanceof ApiError ? failure.message : "Suppression refusée."),
                )
              }
            >
              Demander la suppression
            </Button>
            <Button
              variant="quiet"
              onClick={() =>
                void call("cancelDeletion", { params: { child_id: child.child_id } }).catch(() =>
                  setError("Annulation impossible."),
                )
              }
            >
              Annuler une suppression en cours
            </Button>
          </details>
        </Card>
      ))}
    </Screen>
  );
}
